<template>
  <div id="app" class="app" :class="className">
    <TheSvgInlineResource/>
    <TheUdnLogo
      title="聯合報"
      target="_self"
      color="rgba(0, 0, 0, 0.8)"
      @click.native.prevent="onLogoClick"/>
    <TheMenu
      title="看懂2020 - 數據看選舉"
      hamburger-color="#222222"
      :project="projectName"
      :url="projectUrl"/>
    <TheNav/>
    <TheMobileNav/>
    <main class="main">
      <TheLogotype/>
      <PageTransition>
        <!-- <keep-alive> -->
        <router-view :key="$route.params.year"/>
        <!-- </keep-alive> -->
      </PageTransition>
      <TheComments :url="projectUrl"/>
      <TheFooter/>
    </main>
  </div>
</template>

<script>
import debounce from '@/scripts/debounce'
import throttle from '@/scripts/throttle'
import TheSvgInlineResource from '@/components/TheSvgInlineResource'
import TheUdnLogo from '@/components/TheUdnLogo'
import TheMenu from '@/components/TheMenu'
import TheNav from '@/components/TheNav'
import TheMobileNav from '@/components/TheMobileNav'
import TheLogotype from '@/components/TheLogotype'
import TheComments from '@/components/TheComments'
import TheFooter from '@/components/TheFooter'
import PageTransition from '@/components/PageTransition'
import { PROJECT_NAME, PROJECT_URL } from '@/constant'

export default {
  name: 'App',
  components: {
    TheSvgInlineResource,
    TheUdnLogo,
    TheMenu,
    TheNav,
    TheMobileNav,
    TheLogotype,
    TheComments,
    TheFooter,
    PageTransition
  },
  provide () {
    return {
      projectUrl: this.projectUrl,
      baseUrl: this.baseUrl,
      isDesktop: this.isDesktop,
      isMobile: this.isMobile,
      isInApp: this.isInApp
    }
  },
  data () {
    let baseUrl = process.env.BASE_URL
    if (baseUrl !== '/') {
      baseUrl = `${baseUrl}/`
    }
    let isDesktop = this.$device.desktop()
    let isMobile = this.$device.mobile()
    let isInApp = this.$inApp.isInApp
    return {
      projectName: PROJECT_NAME,
      projectUrl: PROJECT_URL,
      baseUrl,
      isDesktop,
      isMobile,
      isInApp,
      innerWidth: 0,
      innerHeight: 0,
      pageYOffset: 0,
      startDragPoint: null,
      lastDragPoint: null
    }
  },
  computed: {
    className () {
      let isNarrowDesktop = this.innerWidth >= 960 && this.innerHeight <= 768
      return {
        'narrow-desktop': isNarrowDesktop,
        'is-scroll-top': this.isScrollTop,
        'is-scroll-bottom': this.isScrollBottom
      }
    },
    isScrollTop () {
      return this.pageYOffset <= 0
    },
    isScrollBottom () {
      let bottom = document.body.clientHeight - this.innerHeight
      if (bottom <= 0) {
        return false
      }
      return this.pageYOffset >= bottom
    }
  },
  methods: {
    onResize: debounce(function () {
      this.syncViewportSize()
      this.$bus.$emit('resize', { width: this.innerWidth, height: this.innerHeight })
    }, 50),
    onScroll: throttle(function () {
      this.syncViewportScroll()
      // window.innerWidth > 520 && (this.isSearchPanelOpened = false)
    }, 200),
    onLogoClick () {
      this.$router
        .push('/')
        .catch(err => err)
    },
    onDragStart (event) {
      let point = this.startDragPoint = this.detectDragPoint(event)
      document.addEventListener('touchmove', this.onDragMove)
      document.addEventListener('touchend', this.onDragStop)
      document.addEventListener('touchcancel', this.onDragStop)
      document.addEventListener('mousemove', this.onDragMove)
      document.addEventListener('mouseup', this.onDragStop)
      this.$bus.$emit('drag:start', { ...point })
    },
    onDragMove (event) {
      const point = this.lastDragPoint = this.detectDragPoint(event)
      this.$bus.$emit('drag:move', { ...point })
    },
    onDragStop (event) {
      document.removeEventListener('touchmove', this.onDragMove)
      document.removeEventListener('touchend', this.onDragStop)
      document.removeEventListener('touchcancel', this.onDragStop)
      document.removeEventListener('mousemove', this.onDragMove)
      document.removeEventListener('mouseup', this.onDragStop)
      const point = this.detectDragPoint(event)
      this.$bus.$emit('drag:stop', { ...point })
    },
    detectDragPoint (event, fromPageOrigin = false) {
      const isTouch = event.type.indexOf('touch') === 0
      const point = !isTouch ? event : event.touches[0] || event.changedTouches[0]
      if (!point) {
        return null
      }
      let x, y
      if (!fromPageOrigin) {
        x = point.clientX
        y = point.clientY
      } else {
        x = point.pageX
        y = point.pageY
      }
      return { x, y, isTouch }
    },
    syncViewportSize () {
      this.innerWidth = window.innerWidth
      this.innerHeight = window.innerHeight
    },
    syncViewportScroll () {
      this.pageYOffset = window.pageYOffset
    }
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('touchstart', this.onDragStart)
    if (!this.isMobile) {
      window.removeEventListener('mousedown', this.onDragStart)
    }
  },
  mounted () {
    window.addEventListener('resize', this.onResize)
    window.addEventListener('scroll', this.onScroll)
    window.addEventListener('touchstart', this.onDragStart)
    if (!this.isMobile) {
      window.addEventListener('mousedown', this.onDragStart)
    }
    this.syncViewportSize()
    this.syncViewportScroll()
  }
}
</script>

<style lang="scss">
.app {
  background: url(~@/assets/body-bg.png) repeat top center;
  overflow-x: hidden;
}

.main {
  min-height: 100vh;
  max-width: 420px;
  margin: 0 auto;
  padding-bottom: 44px;

  @include rwd($RWD_TABLET_SMALL) {
    // max-width: 480px;
  }
  @include rwd($RWD_TABLET) {
  }
  @include rwd($RWD_DESKTOP) {
    width: 50%;
    max-width: 720px;
  }
  @include rwd($RWD_DESKTOP_WIDE) {
    width: auto;
    max-width: none;
    margin: 0 150px;
  }
  @include rwd($RWD_DESKTOP_1440) {
    width: 1140px;
    margin: 0 auto;
    padding: 0;
  }
}
</style>
